import { useMemo, useState } from "react";

import {
    AbfLoanExpanded,
    LoanInfoSorting,
    getZcCollateral,
    getZcLedger,
    serializeStrategyDuration,
    useBestQuotes,
    useStrategyDurations,
    useLoanRefinanceTransaction,
    getCollateralMintToInfo,
    LoanFilterType,
    LoopPositionExpanded,
    marketsPublicApi,
    getZcLoanCollateral,
    RefinanceOption,
    isLoopLoan,
    summarizeLedgerAccount,
    getZcLoanLiquidationThreshold,
    getZcLoanLtv
} from "@bridgesplit/abf-react";
import { EmptyPlaceholder, Row, Column, Text, Button, DialogWrapper, OptionSelectList } from "@bridgesplit/ui";
import {
    LOADING_ERROR,
    Result,
    SPACE,
    SortDirection,
    decimalsToBps,
    findMaxElement,
    formatDate,
    formatPercent,
    snakeCaseToTitleCase,
    uiAmountToLamports
} from "@bridgesplit/utils";
import { formatDurationWithType, getDurationIndex, RefinanceLoanTxnParams } from "@bridgesplit/abf-sdk";
import { useAppNavigate } from "@bridgesplit/react";
import { useLocation } from "react-router-dom";
import { LOAN_SLUG, COPY } from "app/constants";
import { TrackTransactionEvent, TransactionTypes } from "app/types";
import { trackSubmitRefinance } from "app/hooks";
import { AppDialog, useAppDialog } from "app/utils";
import { DialogHeader, DialogHeaderWithBack } from "app/components/common";

import { useTransactionSender } from "../../transactions";
import { AppButton, getLoanAssetPath } from "../../common";
import { calculateOptionsWithOffsets } from "./payments/hooks/utils";
import { LoanPositionStats } from "./LoanPositionStats";
const REFINANCE_LTV_BUFFER = decimalsToBps(0.05);

export function getRefinanceMinLqt(loanExpanded: AbfLoanExpanded | undefined) {
    const currentLqt = decimalsToBps(getZcLoanLiquidationThreshold(loanExpanded) ?? 0);
    const currentLtv = decimalsToBps(getZcLoanLtv(loanExpanded) ?? 0);
    const refinanceLqt = Math.min(currentLqt, currentLtv + REFINANCE_LTV_BUFFER);
    return refinanceLqt;
}

export default function RefinanceLoan({
    loanExpanded,
    loopPosition
}: {
    loanExpanded: AbfLoanExpanded | undefined;
    loopPosition: LoopPositionExpanded | undefined;
}) {
    const firstLedger = getZcLedger(loanExpanded);
    const newPrincipalAmount = firstLedger?.ledgerDebt.total ?? 0; // New principal amount is the total debt of the loan
    const currentCollateralAmount = getZcLoanCollateral(loanExpanded)?.amount ?? 0;

    const { strategyDurations } = useStrategyDurations();

    const { data: quotes } = useBestQuotes({
        collateralMintToInfo: getCollateralMintToInfo(loanExpanded),
        principalMint: firstLedger?.principalMint,
        strategyDurations,
        minPrincipalAmountLamports: uiAmountToLamports(newPrincipalAmount, loanExpanded?.principalMetadata.decimals),
        loanAddress: loanExpanded?.loan.address,
        showSelf: true,
        refinanceParams:
            firstLedger?.strategy !== undefined
                ? {
                      refinanceStrategy: firstLedger?.strategy,
                      // Backend self-refi gate: `available >= (min_principal + additional_borrow) - principal_repayment`.
                      // Sending 0 asks the source strategy for the full principal as new idle capital and drops all quotes.
                      principalRepayment: uiAmountToLamports(
                          summarizeLedgerAccount(firstLedger).principalOutstanding,
                          loanExpanded?.principalMetadata.decimals
                      ),
                      minLqt: getRefinanceMinLqt(loanExpanded),
                      ledgerIndex: 0
                  }
                : undefined
    });

    const [optionState, setOption] = useState<RefinanceOption>();

    const optionsWithOffsets = useMemo(
        () =>
            calculateOptionsWithOffsets({
                quotes,
                strategyDurations,
                newPrincipalAmount,
                loanExpanded
            }),
        [loanExpanded, newPrincipalAmount, quotes, strategyDurations]
    );

    const validOptions = optionsWithOffsets?.filter((o) => !!o.bestQuote);
    const option = optionState ?? validOptions?.[0];
    const refinance = useRefinance(loanExpanded);
    if (validOptions?.length === 0) {
        return (
            <EmptyPlaceholder
                header="No available terms"
                description={
                    validOptions?.length
                        ? "Your loan health is too low to be adjusted"
                        : "There aren't any offers matching your loan terms"
                }
            />
        );
    }
    const isLoop = isLoopLoan(loanExpanded);

    return (
        <>
            <UpdateTermsSelect
                option={option}
                validOptions={validOptions}
                optionsWithOffsets={optionsWithOffsets}
                setOption={setOption}
            />
            <LoanPositionStats
                loanExpanded={loanExpanded}
                loopPosition={loopPosition}
                newPrincipalAmount={option?.totalDebt ?? newPrincipalAmount}
                newCollateralAmount={currentCollateralAmount}
                isFullRepay={false}
                loading={!option}
                newDueDate={option?.loanEndDate}
                newBorrowApy={option?.bestQuote?.apy}
                newStrategyLtv={option?.bestQuote?.ltv}
                newLiquidationThreshold={option?.bestQuote?.liquidationThreshold}
                show={{
                    debtAndCollateral: true,
                    health: !!option,
                    netApyCollapsable: isLoop && !!option,
                    borrowApy: !isLoop && !!option
                }}
            />

            <AppButton
                isTransaction
                transactionType={TransactionTypes.Refinance}
                disabled={!newPrincipalAmount || !option}
                asyncCta={{ onClickWithResult: () => refinance(newPrincipalAmount, option), closeDialog: "onSuccess" }}
            >
                {snakeCaseToTitleCase(COPY.REFINANCE_TERM, SPACE)}
            </AppButton>
        </>
    );
}

export function UpdateTermsSelectSettings({
    option,
    validOptions,
    optionsWithOffsets,
    setOption,
    isNestedDialogView = false,
    onClose
}: {
    option: RefinanceOption | undefined;
    validOptions: RefinanceOption[] | undefined;
    optionsWithOffsets: RefinanceOption[] | undefined;
    setOption: (option: RefinanceOption | undefined) => void;
    isNestedDialogView?: boolean;
    onClose: () => void;
}) {
    const [editingOption, setEditingOption] = useState<RefinanceOption | undefined>(option);
    const component = useMemo(() => {
        return (
            <Column spacing={2}>
                <UpdateTermsSelect
                    option={editingOption}
                    validOptions={validOptions}
                    optionsWithOffsets={optionsWithOffsets}
                    setOption={setEditingOption}
                />
                <Button
                    variant="contained"
                    disabled={!editingOption}
                    onClick={() => {
                        setOption(editingOption);
                        onClose();
                    }}
                >
                    Adjust terms
                </Button>
            </Column>
        );
    }, [validOptions, optionsWithOffsets, setEditingOption, onClose, editingOption, setOption]);

    return (
        <>
            {!isNestedDialogView ? (
                <DialogWrapper>
                    <DialogHeader
                        headerVariant="h4"
                        header={"Update terms"}
                        description="Select a duration to fix the borrow rate on the total debt. Rate may vary based on the additional amount borrowed."
                    />
                    {component}
                </DialogWrapper>
            ) : (
                <>
                    <Column spacing={2}>
                        <DialogHeaderWithBack header={"Update terms"} back={onClose} leftAlignHeader />
                        <Text variant="body2" color="caption">
                            Select a duration to fix the borrow rate on the total debt. Rate may vary based on the
                            additional amount borrowed.
                        </Text>
                    </Column>
                    {component}
                </>
            )}
        </>
    );
}

export function UpdateTermsDialog() {
    const { getData, close } = useAppDialog();
    const data = getData(AppDialog.UpdateTerms);
    if (!data) return null;
    return (
        <UpdateTermsSelectSettings
            option={data.option}
            validOptions={data.validOptions}
            optionsWithOffsets={data.optionsWithOffsets}
            setOption={data.setOption}
            onClose={() => close()}
        />
    );
}

export function RenderDuration({ option: { strategyDuration, loanEndDate } }: { option: RefinanceOption }) {
    return (
        <Row spacing={1}>
            <Text>{formatDurationWithType(strategyDuration)}</Text>
            <Text color="caption">{formatDate(loanEndDate, "date", { showTime: "never" })}</Text>
        </Row>
    );
}

function useRefinance(loanExpanded: AbfLoanExpanded | undefined) {
    const send = useTransactionSender();
    const refinanceLoan = useLoanRefinanceTransaction();

    const [fetchLoans] = marketsPublicApi.useLazyLoanInfosQuery();
    const location = useLocation();
    const navigate = useAppNavigate();

    return async (principalAmount: number, option: RefinanceOption | undefined) => {
        if (!loanExpanded || !option?.bestQuote || !principalAmount) return Result.errFromMessage(LOADING_ERROR);

        const quote = option.bestQuote;

        const durationIndex = getDurationIndex({ duration: quote.duration, durationType: quote.durationType });

        if (durationIndex === undefined) return Result.errFromMessage(LOADING_ERROR);

        const firstLedger = getZcLedger(loanExpanded);
        const firstCollateral = getZcCollateral(loanExpanded);

        if (!firstLedger?.principalMint || !firstCollateral) return Result.errFromMessage(LOADING_ERROR);

        const params: RefinanceLoanTxnParams = {
            newStrategy: quote.lendingStrategyAddress,
            principalMint: firstLedger.principalMint,
            oldStrategy: firstLedger.strategy,
            loan: loanExpanded.loan.address,
            refinanceParams: {
                durationIndex,
                ledgerIndex: 0 //TODO: Handle multiple ledger index input after multiple collateral is supported
            }
        };

        // navigate to new refinanced loan only if on loan detail page
        async function onSuccess() {
            if (!loanExpanded || !location.pathname.includes(LOAN_SLUG) || !firstLedger?.principalMint) return;
            const loans = await fetchLoans({
                borrowers: [loanExpanded?.borrower],
                principalMints: [loanExpanded?.principalMetadata.mint],
                filterType: LoanFilterType.Active,
                loanAddresses: [loanExpanded.loan.address],
                lenders: [],
                sortSide: SortDirection.Asc,
                sortType: LoanInfoSorting.EndTime,
                collateralMints: [],
                collateralIdentifiers: [],
                orderFundingTypes: loanExpanded.loanType
            });
            if (!loans.data) return;
            const lastLoan = findMaxElement(loans.data.items, (v) => v.loan.id);

            if (!lastLoan) return;
            navigate(getLoanAssetPath(lastLoan.loan));
        }

        return await send(refinanceLoan, params, {
            description: "Adjusting terms",
            sendOptions: { onSuccess },
            mixpanelEvent: {
                key: TrackTransactionEvent.SubmitRefinance,
                params: trackSubmitRefinance(params, loanExpanded, 0)
            }
        });
    };
}

export function UpdateTermsSelect({
    option,
    validOptions,
    optionsWithOffsets,
    setOption
}: {
    option: RefinanceOption | undefined;
    validOptions: RefinanceOption[] | undefined;
    optionsWithOffsets: RefinanceOption[] | undefined;
    setOption: (option: RefinanceOption | undefined) => void;
}) {
    const options = optionsWithOffsets ?? [];

    return (
        <OptionSelectList<RefinanceOption>
            label="Fixed rate for"
            loading={!validOptions}
            skeletonCount={4}
            options={options}
            getKey={(opt) => serializeStrategyDuration(opt.strategyDuration)}
            isSelected={(opt) =>
                option != null &&
                serializeStrategyDuration(option.strategyDuration) === serializeStrategyDuration(opt.strategyDuration)
            }
            isDisabled={(opt) => !opt.bestQuote}
            onSelect={(opt) => {
                const found = validOptions?.find(
                    (o) =>
                        serializeStrategyDuration(o.strategyDuration) ===
                        serializeStrategyDuration(opt.strategyDuration)
                );
                if (found) setOption(found);
            }}
        >
            {(opt) => <RefinanceOptionButtonContent option={opt} />}
        </OptionSelectList>
    );
}

function RefinanceOptionButtonContent({ option }: { option: RefinanceOption }) {
    return (
        <Row spaceBetween sx={{ width: "100%" }}>
            <RenderDuration option={option} />
            <Text color={!option.bestQuote ? "disabled" : "body"}>
                {!option.bestQuote ? "No liquidity" : formatPercent(option.bestQuote?.apy)}
            </Text>
        </Row>
    );
}
